import { useEffect, useRef, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useNavigate } from "react-router-dom";
import { useOrganizationList } from "@clerk/react";
import { Building2Icon, PlusIcon, Trash2Icon, UploadIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDashboardStore } from "@/stores";
import { clerkErrorMessage } from "@/lib/clerk-errors";

// What Clerk accepts for an organization logo.
const LOGO_TYPES = "image/png,image/jpeg,image/gif,image/webp";
const LOGO_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Create a team (a Clerk organization) with an optional logo, then switch
 * to it the way the team switcher does. Clerk generates the slug and
 * enforces the per-user creation limit.
 */
export function CreateTeamDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { createOrganization, setActive } = useOrganizationList();
  const { resetStore } = useDashboardStore();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!logo) {
      setLogoPreview(null);
      return;
    }
    const url = URL.createObjectURL(logo);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logo]);

  const trimmedName = name.trim();

  const handleCreate = async () => {
    if (!trimmedName || creating || !createOrganization || !setActive) return;
    setCreating(true);
    setError(null);
    try {
      const organization = await createOrganization({ name: trimmedName });
      if (logo) {
        try {
          await organization.setLogo({ file: logo });
        } catch {
          toast.warning("Team created, but the logo didn't upload. Add it from Settings → Organization.");
        }
      }
      await setActive({ organization: organization.id });
      resetStore();
      toast.success(`Created ${organization.name}`);
      onOpenChange(false);
      navigate("/dashboard/");
    } catch (err) {
      setError(clerkErrorMessage(err, "Couldn't create the team. Try again."));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nowOpen) => {
        if (creating) return;
        onOpenChange(nowOpen);
        // Reset on open, not close, so the form doesn't blank while fading out.
        if (nowOpen) {
          setName("");
          setLogo(null);
          setError(null);
        }
      }}
    >
      <DialogContent className="rounded-xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create a team</DialogTitle>
          <DialogDescription>
            A team has its own campaigns, members and plan. You'll switch to it
            once it's created.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4">
          <Avatar variant="square" className="size-14 rounded-lg">
            {logoPreview && (
              <AvatarImage src={logoPreview} alt="Team logo" />
            )}
            <AvatarFallback>
              <Building2Icon className="size-5 text-muted-foreground" />
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={creating}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadIcon />
                {logo ? "Replace logo" : "Upload logo"}
              </Button>
              {logo && (
                <Button
                  type="button"
                  variant="destructive-ghost"
                  size="sm"
                  disabled={creating}
                  onClick={() => setLogo(null)}
                >
                  <Trash2Icon />
                  Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Optional. PNG, JPG, GIF or WEBP, up to 10 MB.
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={LOGO_TYPES}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              if (file.size > LOGO_MAX_BYTES) {
                toast.error("The logo must be 10 MB or smaller");
                return;
              }
              setLogo(file);
            }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="create_team_name">Team name</Label>
          <Input
            id="create_team_name"
            autoFocus
            placeholder="Acme Inc."
            value={name}
            disabled={creating}
            aria-invalid={!!error}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleCreate();
            }}
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" disabled={creating}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={() => void handleCreate()} disabled={!trimmedName || creating}>
            {creating ? <Spinner /> : <PlusIcon />}
            Create team
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
